import { CapacityIcon, HotelLocation, ImageIcon } from '../../icons/icons';
import { serverUrl } from '../../../config';

require('./index.css').toString();

const NUMERIC_FIELDS = [
  { key: 'numOfBedrooms', label: 'GUEST ROOMS', displayLabel: 'Guest Rooms' },
  { key: 'numOfMeetingRooms', label: 'MEETING ROOMS', displayLabel: 'Meeting Rooms' },
  { key: 'maxCapacity', label: 'MAX CAP', displayLabel: 'Max Cap' },
];

const MIN_SELECTED = 2;
const MAX_SELECTED = 4;

function decodeHTML(str) {
  if (!str) return '';
  const el = document.createElement('textarea');
  el.innerHTML = str;
  return el.value;
}

export default class AtAGlance {

  static get toolbox() {
    return {
      icon: CapacityIcon,
      title: 'At A Glance',
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, api, readOnly, block }) {
    this.api = api;
    this.readOnly = readOnly;
    this.block = block;
    this.config = config;

    this._data = {
      emmaId: data.emmaId || '',
      hotelName: data.hotelName || '',
      title: data.title || '',
      description: data.description || '',
      selectedItems: data.selectedItems || [],
      apiData: data.apiData || null,
      imageOverrides: data.imageOverrides || {},
    };
  }

  render() {
    this._container = document.createElement('div');
    this._container.classList.add('at-a-glance-container');

    // hidden input to prevent empty block creation
    const hiddenInput = document.createElement('input');
    hiddenInput.setAttribute('style', 'visibility:hidden;width:0;height:0;padding:0;margin:0;border:0;position:absolute');
    this._container.appendChild(hiddenInput);

    this._renderConfigured();

    // Configure button + modal (edit mode only)
    if (!this.readOnly) {
      this._configureBtn = document.createElement('button');
      this._configureBtn.classList.add('at-a-glance-configure-btn');
      this._configureBtn.textContent = 'Configure';
      this._configureBtn.addEventListener('click', () => this._showModal());
      this._container.appendChild(this._configureBtn);

      this._modal = this._buildModal();
      this._container.appendChild(this._modal);
    }

    return this._container;
  }

  _getHotelDetails() {
    const api = this._data.apiData;
    if (!api) return null;

    const hotelInfo = api?.hotelInfo?.collection?.[0];
    const roomInfo = api?.hotelRoomInfo?.collection?.[0];
    const content = hotelInfo?.content || {};
    const picked = api?.assets?.picked || {};

    const location = hotelInfo?.metadata?.location?.values?.[0]?.description || '';

    return {
      name: decodeHTML(this._data.hotelName || ''),
      title: this._data.title || decodeHTML(content?.text?.content?.title || ''),
      description: this._data.description || decodeHTML(content?.text?.content?.shortDescription || ''),
      city: decodeHTML(hotelInfo?.metadata?.city?.description || ''),
      country: decodeHTML(hotelInfo?.metadata?.country?.description || ''),
      brand: decodeHTML(hotelInfo?.metadata?.brand?.description || ''),
      brandCode: hotelInfo?.metadata?.brand?.key || '',
      location: decodeHTML(location),
      url: hotelInfo?.linkUrl || '',
      heroImage: this._data.imageOverrides.hero || content?.image?.binaryContent?.url || '',
      images: {
        guestRoom: this._data.imageOverrides.guestRoom || picked?.guestRoom?.links?.cdnLinks?.[0] || '',
        meetingRoom: this._data.imageOverrides.meetingRoom || picked?.meetingRoom?.links?.cdnLinks?.[0] || '',
        restaurant: this._data.imageOverrides.restaurant || picked?.restaurant?.links?.cdnLinks?.[0] || '',
        gym: this._data.imageOverrides.gym || picked?.gym?.links?.cdnLinks?.[0] || '',
      },
    };
  }

  _createReplaceBtn(imageSlot) {
    const IMAGE_SLOT_FILE_INDEX = {
      hero: 0,
      guestRoom: 1,
      meetingRoom: 2,
      restaurant: 3,
      gym: 4,
    };

    const btn = document.createElement('button');
    btn.classList.add('aag-replace-btn');
    btn.innerHTML = ImageIcon;
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this._pendingImageSlot = imageSlot;
      const blockIndex = this.api.blocks.getCurrentBlockIndex();
      const block = this.api.blocks.getBlockByIndex(blockIndex);

      // Get file restrictions for this image slot from layout options
      const fileIndex = IMAGE_SLOT_FILE_INDEX[imageSlot];

      const layoutFiles = this.config?.layoutFiles;
      const slotFiles = Array.isArray(layoutFiles) && layoutFiles[fileIndex]
        ? layoutFiles[fileIndex]
        : undefined;

      this.config.showMediaLibrary({
        details: block,
        data: this._data,
        bundle: 'image',
        imageSlot: imageSlot,
        slideFiles: slotFiles,
      });
    });
    return btn;
  }

  _createResetBtn(imageSlot) {
    const btn = document.createElement('button');
    btn.classList.add('aag-reset-btn');
    btn.title = 'Reset to original';
    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" width="16" height="16"><path d="M440-122q-121-15-200.5-105.5T160-440q0-66 26-126.5T260-672l57 57q-38 34-57.5 79T240-440q0 88 56 155.5T440-202v80Zm80 0v-80q87-16 143.5-83T720-440q0-100-70-170t-170-70h-3l44 44-56 56-140-140 140-140 56 56-44 44h3q134 0 227 93t93 227q0 121-79.5 211.5T520-122Z" fill="currentColor"/></svg>`;
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      delete this._data.imageOverrides[imageSlot];
      this._renderConfigured();
      if (this.block?.dispatchChange) {
        this.block.dispatchChange();
      }
    });

    if (!this._data.imageOverrides[imageSlot]) {
      btn.style.display = 'none';
    }

    return btn;
  }

  onMediaSelected(media) {
    if (!this._pendingImageSlot || !media?.field_media_image) return;
    this._data.imageOverrides[this._pendingImageSlot] = media.field_media_image;
    this._pendingImageSlot = null;
    this._renderConfigured();
    if (this.block?.dispatchChange) {
      this.block.dispatchChange();
    }
  }

  _renderConfigured() {
    if (this._contentEl) {
      this._contentEl.remove();
    }

    this._contentEl = document.createElement('div');
    this._contentEl.classList.add('at-a-glance-content-container');

    const hotel = this._getHotelDetails();

    if (!hotel && this._data.selectedItems.length === 0) {
      const placeholder = document.createElement('div');
      placeholder.style.cssText = 'color:#999;font-size:16px;';
      placeholder.textContent = 'Click "Configure" to set up At A Glance';
      this._contentEl.appendChild(placeholder);
      this._container.appendChild(this._contentEl);
      return;
    }

    // Left column: hero image + overlay (brand logo, title, description)
    const leftCol = document.createElement('div');
    leftCol.classList.add('at-a-glance-left');

    if (hotel?.heroImage) {
      const heroImg = document.createElement('img');
      heroImg.src = hotel.heroImage;
      heroImg.classList.add('at-a-glance-hero');
      leftCol.appendChild(heroImg);

      if (!this.readOnly && this.config?.showMediaLibrary) {
        leftCol.appendChild(this._createReplaceBtn('hero'));
        leftCol.appendChild(this._createResetBtn('hero'));
      }
    }

    if (hotel) {
      const overlay = document.createElement('div');
      overlay.classList.add('content_block', 'hotelTextOverlay');

      const overlayContent = document.createElement('div');
      overlayContent.classList.add('hotel-text-overlay-content-container');

      if (hotel.brandCode && this.config?.brandLogos?.[hotel.brandCode]) {
        const logoEl = document.createElement('div');
        logoEl.classList.add('hotel-logo-container');
        logoEl.innerHTML = this.config.brandLogos[hotel.brandCode];
        overlayContent.appendChild(logoEl);
      }

      const titleEl = document.createElement('h4');
      titleEl.classList.add('overlay-heading');
      titleEl.innerHTML = hotel.title || '';
      if (!this.readOnly) {
        titleEl.contentEditable = 'true';
        titleEl.dataset.placeholder = 'Overlay tagline';
        titleEl.addEventListener('input', () => {
          this._data.title = titleEl.innerHTML;
        });
      }
      overlayContent.appendChild(titleEl);

      const descEl = document.createElement('p');
      descEl.classList.add('overlay-body');
      descEl.innerHTML = hotel.description || '';
      if (!this.readOnly) {
        descEl.contentEditable = 'true';
        descEl.dataset.placeholder = 'Overlay description';
        descEl.addEventListener('input', () => {
          this._data.description = descEl.innerHTML;
        });
      }
      overlayContent.appendChild(descEl);

      overlay.appendChild(overlayContent);
      leftCol.appendChild(overlay);
    }

    this._contentEl.appendChild(leftCol);

    // Right column: image grid, hotel info, capacity/services, footer
    const rightCol = document.createElement('div');
    rightCol.classList.add('at-a-glance-right');

    // Image grid (2×2)
    if (hotel) {
      const thumbs = Object.entries(hotel.images).filter(([, url]) => url);
      if (thumbs.length > 0) {
        const imageGrid = document.createElement('div');
        imageGrid.classList.add('at-a-glance-image-grid');

        thumbs.forEach(([key, url]) => {
          const container = document.createElement('div');
          container.classList.add('img-container');
          const img = document.createElement('img');
          img.src = url;
          img.alt = key;
          container.appendChild(img);

          if (!this.readOnly && this.config?.showMediaLibrary) {
            container.appendChild(this._createReplaceBtn(key));
            container.appendChild(this._createResetBtn(key));
          }

          imageGrid.appendChild(container);
        });

        rightCol.appendChild(imageGrid);
      }
    }

    // Hotel info (location header: pin, country, city, name)
    if (hotel) {
      const headingContainer = document.createElement('div');
      headingContainer.classList.add('hotel-heading-container');

      const headingContent = document.createElement('div');
      headingContent.classList.add('hotel-heading-content-container');

      const pinIcon = document.createElement('div');
      pinIcon.classList.add('location-pin-icon');
      pinIcon.innerHTML = HotelLocation;
      headingContent.appendChild(pinIcon);

      if (hotel.country) {
        const countryEl = document.createElement('h6');
        countryEl.classList.add('hotel-country');
        countryEl.textContent = hotel.country;
        headingContent.appendChild(countryEl);
      }

      if (hotel.city) {
        const cityEl = document.createElement('h6');
        cityEl.classList.add('hotel-city');
        cityEl.textContent = hotel.city;
        headingContent.appendChild(cityEl);
      }

      if (hotel.name) {
        const nameEl = document.createElement('h6');
        nameEl.classList.add('hotel-name');
        nameEl.textContent = hotel.name;
        headingContent.appendChild(nameEl);
      }

      headingContainer.appendChild(headingContent);
      rightCol.appendChild(headingContainer);
    }

    // Capacity / services items
    if (this._data.selectedItems.length > 0) {
      const capacityContainer = document.createElement('div');
      capacityContainer.classList.add('hotel-capacity-container');

      const capacityContent = document.createElement('div');
      capacityContent.classList.add('hotel-capacity-content-container');

      this._data.selectedItems.forEach(item => {
        const group = document.createElement('div');
        group.classList.add('hotel-capacity-group');

        if (item.type === 'numeric') {
          const val = document.createElement('div');
          val.classList.add('hotel-capacity-value');
          val.textContent = item.value != null ? Number(item.value).toLocaleString() : '—';
          group.appendChild(val);
        } else {
          const check = document.createElement('div');
          check.classList.add('hotel-capacity-value');
          check.textContent = '✔';
          group.appendChild(check);
        }

        const label = document.createElement('div');
        label.classList.add('hotel-capacity-label');
        const words = item.label.toUpperCase().split(' ');
        if (words.length >= 2) {
          label.innerHTML = words[0] + '<br>' + words.slice(1).join(' ');
        } else {
          label.textContent = item.label.toUpperCase();
        }
        group.appendChild(label);

        capacityContent.appendChild(group);
      });

      capacityContainer.appendChild(capacityContent);
      rightCol.appendChild(capacityContainer);
    }

    // Footer (location + hotel link)
    if (hotel) {
      const footerContainer = document.createElement('div');
      footerContainer.classList.add('hotel-footer-container');

      const footerContent = document.createElement('div');
      footerContent.classList.add('hotel-footer-content-container');

      if (hotel.location) {
        const locContainer = document.createElement('div');
        locContainer.classList.add('hotel-footer-location-container');

        const locLabel = document.createElement('p');
        locLabel.classList.add('hotel-footer-location-label');
        locLabel.textContent = 'LOCATION:';
        locContainer.appendChild(locLabel);

        const locValue = document.createElement('p');
        locValue.classList.add('hotel-footer-location');
        locValue.textContent = hotel.location;
        locContainer.appendChild(locValue);

        footerContent.appendChild(locContainer);
      }

      if (hotel.url) {
        const link = document.createElement('a');
        link.classList.add('hotel-footer-url');
        link.href = `https://radissonhotels.com${hotel.url}`;
        link.target = '_blank';
        link.rel = 'noopener';
        link.textContent = 'VISIT HOTEL WEBSITE';
        footerContent.appendChild(link);
      }

      footerContainer.appendChild(footerContent);
      rightCol.appendChild(footerContainer);
    }

    this._contentEl.appendChild(rightCol);
    this._container.appendChild(this._contentEl);
  }

  _buildModal() {
    const modal = document.createElement('div');
    modal.classList.add('at-a-glance-modal');

    const backdrop = document.createElement('div');
    backdrop.classList.add('at-a-glance-modal-backdrop');
    backdrop.addEventListener('click', () => this._hideModal());

    const inner = document.createElement('div');
    inner.classList.add('at-a-glance-modal-inner');

    const heading = document.createElement('h4');
    heading.textContent = 'At A Glance Settings';

    // EMMA ID row (label, input + fetch button)
    const emmaLabel = document.createElement('label');
    emmaLabel.textContent = 'EMMA ID';

    const emmaRow = document.createElement('div');
    emmaRow.classList.add('at-a-glance-emma-row');

    this._emmaInput = document.createElement('input');
    this._emmaInput.type = 'text';
    this._emmaInput.placeholder = 'e.g. ABC123';
    this._emmaInput.value = this._data.emmaId;

    this._fetchBtn = document.createElement('button');
    this._fetchBtn.classList.add('at-a-glance-fetch-btn');
    this._fetchBtn.textContent = 'Load Hotel Data';
    this._fetchBtn.addEventListener('click', () => this._fetchData());

    emmaRow.appendChild(this._emmaInput);
    emmaRow.appendChild(this._fetchBtn);

    // Status message
    this._statusEl = document.createElement('div');
    this._statusEl.classList.add('at-a-glance-status');

    // Selection area (hidden until data fetched)
    this._selectionArea = document.createElement('div');
    this._selectionArea.style.display = 'none';

    // Actions
    const actions = document.createElement('div');
    actions.classList.add('at-a-glance-modal-actions');

    const cancelBtn = document.createElement('button');
    cancelBtn.classList.add('aag-cancel-btn');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => this._hideModal());

    this._applyBtn = document.createElement('button');
    this._applyBtn.classList.add('aag-apply-btn');
    this._applyBtn.textContent = 'Apply';
    this._applyBtn.addEventListener('click', () => this._applySettings());

    actions.appendChild(cancelBtn);
    actions.appendChild(this._applyBtn);

    inner.appendChild(heading);
    inner.appendChild(emmaLabel);
    inner.appendChild(emmaRow);
    inner.appendChild(this._statusEl);
    inner.appendChild(this._selectionArea);
    inner.appendChild(actions);

    modal.appendChild(backdrop);
    modal.appendChild(inner);

    return modal;
  }

  async _fetchData() {
    const emmaId = this._emmaInput.value.trim();
    if (!emmaId) {
      this._statusEl.textContent = 'Please enter an EMMA ID.';
      this._statusEl.classList.add('error');
      return;
    }

    this._fetchBtn.disabled = true;
    this._fetchBtn.textContent = 'Fetching...';
    this._statusEl.textContent = '';
    this._statusEl.classList.remove('error');

    try {
      const url = `${serverUrl}/api/v2/rhg/map?endpoint=at-a-glance&emma=${encodeURIComponent(emmaId)}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const apiData = await res.json();
      this._data.apiData = apiData;
      this._data.emmaId = emmaId;

      // Extract hotel name from hotelInfo or hotelRoomInfo
      const hotelInfo = apiData?.hotelInfo?.collection?.[0];
      const roomInfo = apiData?.hotelRoomInfo?.collection?.[0];
      this._data.hotelName = decodeHTML(
        hotelInfo?.metadata?.tmsHotelCode?.description ||
        roomInfo?.metadata?.tmsHotelCode?.description ||
        roomInfo?.content?.hotelName ||
        ''
      );

      // Set title/description from API if not already overridden
      const hotelContent = hotelInfo?.content || {};
      if (!this._data.title) {
        this._data.title = decodeHTML(hotelContent?.text?.content?.title || '');
      }
      if (!this._data.description) {
        this._data.description = decodeHTML(hotelContent?.text?.content?.shortDescription || '');
      }

      this._statusEl.textContent = this._data.hotelName
        ? `Loaded: ${this._data.hotelName}`
        : 'Data loaded successfully.';

      this._buildSelectionUI(apiData);
    } catch (err) {
      this._statusEl.textContent = `Error: ${err.message}`;
      this._statusEl.classList.add('error');
    } finally {
      this._fetchBtn.disabled = false;
      this._fetchBtn.textContent = 'Fetch';
    }
  }

  _buildSelectionUI(apiData) {
    this._selectionArea.innerHTML = '';
    this._selectionArea.style.display = 'block';
    this._checkboxes = [];

    const roomContent = apiData?.hotelRoomInfo?.collection?.[0]?.content || {};
    const hotelContent = apiData?.hotelInfo?.collection?.[0]?.content || {};

    // Numeric capacity data from hotelRoomInfo content
    const content = roomContent;

    // Services from hotelInfo.collection[0].content.service.values[]
    const serviceValues = hotelContent?.service?.values || apiData?.services || [];
    const services = serviceValues.map(s => s?.service || s).filter(Boolean);

    // Previously selected keys for restoring state
    const prevKeys = new Set(this._data.selectedItems.map(i => i.key));
    const hasExistingSelection = prevKeys.size > 0;

    // Count available numeric fields for default selection logic
    const availableNumericCount = NUMERIC_FIELDS.filter(f => content[f.key] != null).length;
    const defaultServiceSlots = MAX_SELECTED - availableNumericCount;

    // Numeric items
    const numHeading = document.createElement('div');
    numHeading.classList.add('at-a-glance-selection-heading');
    numHeading.textContent = 'Room Information';
    this._selectionArea.appendChild(numHeading);

    const numList = document.createElement('ul');
    numList.classList.add('at-a-glance-selection-list');

    NUMERIC_FIELDS.forEach((field) => {
      const value = content[field.key];
      if (value == null) return;

      const checked = hasExistingSelection ? prevKeys.has(field.key) : true;
      const li = this._createSelectionItem({
        type: 'numeric',
        key: field.key,
        label: field.label,
        displayLabel: field.displayLabel,
        value: value,
        checked,
      });
      numList.appendChild(li);
    });

    this._selectionArea.appendChild(numList);

    // Service items
    if (services.length > 0) {
      const svcHeading = document.createElement('div');
      svcHeading.classList.add('at-a-glance-selection-heading');
      svcHeading.textContent = 'Services';
      this._selectionArea.appendChild(svcHeading);

      const svcList = document.createElement('ul');
      svcList.classList.add('at-a-glance-selection-list');

      services.forEach((svc, idx) => {
        const name = decodeHTML(typeof svc === 'string' ? svc : (svc.description || svc.name || svc.label || String(svc)));
        const key = typeof svc === 'string' ? svc : (svc.key || svc.description || `service_${idx}`);
        const checked = hasExistingSelection
          ? prevKeys.has(key)
          : idx < defaultServiceSlots;

        const li = this._createSelectionItem({
          type: 'service',
          key,
          label: name,
          checked,
        });
        svcList.appendChild(li);
      });

      this._selectionArea.appendChild(svcList);
    }

    this._updateCheckboxStates();
  }

  _createSelectionItem({ type, key, label, displayLabel, value, checked }) {
    const li = document.createElement('li');
    li.classList.add('at-a-glance-selection-item');

    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = checked;
    cb.dataset.type = type;
    cb.dataset.key = key;
    cb.dataset.label = label;
    if (value != null) cb.dataset.value = value;

    cb.addEventListener('change', () => this._updateCheckboxStates());

    const labelEl = document.createElement('span');
    labelEl.classList.add('item-label');
    labelEl.textContent = displayLabel || label;

    li.appendChild(cb);
    li.appendChild(labelEl);

    if (type === 'numeric' && value != null) {
      const valEl = document.createElement('span');
      valEl.classList.add('item-value');
      valEl.textContent = Number(value).toLocaleString();
      li.appendChild(valEl);
    }

    this._checkboxes.push({ cb, li });
    return li;
  }

  _getSelectedCount() {
    return this._checkboxes.filter(({ cb }) => cb.checked).length;
  }

  _updateCheckboxStates() {
    const count = this._getSelectedCount();

    this._checkboxes.forEach(({ cb, li }) => {
      if (!cb.checked && count >= MAX_SELECTED) {
        li.classList.add('disabled');
        cb.disabled = true;
      } else if (cb.checked && count <= MIN_SELECTED) {
        li.classList.add('disabled');
        cb.disabled = true;
      } else {
        li.classList.remove('disabled');
        cb.disabled = false;
      }
    });

    // Update apply button
    if (this._applyBtn) {
      this._applyBtn.disabled = count < MIN_SELECTED;
    }
  }

  _applySettings() {
    const selected = this._checkboxes
      .filter(({ cb }) => cb.checked)
      .map(({ cb }) => {
        const item = {
          type: cb.dataset.type,
          key: cb.dataset.key,
          label: cb.dataset.label,
        };
        if (cb.dataset.type === 'numeric' && cb.dataset.value != null) {
          item.value = Number(cb.dataset.value);
        }
        return item;
      });

    this._data.selectedItems = selected;
    this._hideModal();
    this._renderConfigured();

    if (this.block?.dispatchChange) {
      this.block.dispatchChange();
    }
  }

  _showModal() {
    if (this._modal) {
      this._emmaInput.value = this._data.emmaId;

      // If we have cached API data, rebuild the selection UI
      if (this._data.apiData) {
        this._buildSelectionUI(this._data.apiData);
        this._statusEl.textContent = this._data.hotelName
          ? `Loaded: ${this._data.hotelName}`
          : 'Data loaded.';
      } else {
        this._selectionArea.style.display = 'none';
        this._selectionArea.innerHTML = '';
        this._statusEl.textContent = '';
      }

      this._modal.classList.add('show');
    }
  }

  _hideModal() {
    if (this._modal) {
      this._modal.classList.remove('show');
    }
  }

  save() {
    return {
      emmaId: this._data.emmaId,
      hotelName: this._data.hotelName,
      title: this._data.title,
      description: this._data.description,
      selectedItems: this._data.selectedItems,
      apiData: this._data.apiData,
      imageOverrides: this._data.imageOverrides,
    };
  }

  validate() {
    return true;
  }

  destroyed() {
    // No cleanup needed
  }
}
